import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../lib/auth";

export default function BulkImport() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user.role !== "Director") nav("/settings", { replace: true });
  }, [user, nav]);

  const downloadTemplate = async () => {
    // Use the shared `api` axios instance so the bearer token is attached by
    // the existing interceptor (localStorage key is "ach_token" — underscore,
    // not hyphen; earlier code had the wrong key and window.open bypassed
    // auth entirely, silently 401ing).
    setErr("");
    try {
      const r = await api.get("/settings/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ach-machine-import-template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // Blob 4xx responses need to be read as text to get the detail
      let detail = e.message;
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          try { detail = JSON.parse(text).detail || text; } catch { detail = text; }
        } catch { /* fall through */ }
      } else if (e.response?.data?.detail) {
        detail = formatApiErrorDetail(e.response.data.detail);
      }
      setErr(`Couldn't download the template — ${detail}`);
    }
  };

  const runPreview = async () => {
    if (!file) return;
    setBusy(true); setErr(""); setPreview(null); setResult(null);
    try {
      const text = await file.text();
      const r = await api.post("/settings/import/preview", text, { headers: { "Content-Type": "text/csv" }});
      setPreview(r.data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    if (!file) return;
    setCommitting(true); setErr("");
    try {
      const text = await file.text();
      const r = await api.post("/settings/import/commit", text, { headers: { "Content-Type": "text/csv" }});
      setResult(r.data); setPreview(null); setFile(null);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setCommitting(false); }
  };

  return (
    <div data-testid="bulk-import-page">
      <PageHeader
        title="Bulk import machines"
        subtitle="Bring in the whole fleet from a spreadsheet — one row per machine"
        actions={<button className="ach-btn ach-btn--grey" onClick={()=>nav("/settings")} data-testid="bi-back">← Back to Settings</button>}
      />

      <div className="ach-card" style={{marginBottom: 16}}>
        <h2 className="ach-card__title">How this works</h2>
        <ol style={{margin:0, paddingLeft:20, fontSize:13, color:"var(--ach-text-1)", lineHeight:1.7}}>
          <li>Download the template below — it&apos;s a spreadsheet with the columns we need.</li>
          <li>Open it in Excel or Google Sheets and fill one row per machine. Save it as a CSV.</li>
          <li>Upload it here — we&apos;ll show every row and flag anything that doesn&apos;t look right, <strong>before</strong> anything is imported.</li>
          <li>You review, then press <em>Import the good rows</em>. Rows with errors are skipped and listed so you can fix them and try again.</li>
        </ol>
        <div style={{marginTop:14, padding:12, background:"var(--ach-page-bg)", border:"1px dashed var(--ach-card-border)", borderRadius:8, fontSize:12, color:"var(--ach-text-2)"}}>
          <strong>Waiting on the real Viberent export?</strong> This template is a sensible starter set built from the current data model — once we see a real Viberent CSV we&apos;ll adjust the columns to match.
        </div>
        <button className="ach-btn ach-btn--primary" style={{marginTop:14}} onClick={downloadTemplate} data-testid="bi-download-template">↓ Download the template</button>
        {err && err.startsWith("Couldn't download the template") && (
          <div data-testid="bi-download-err" style={{marginTop:12, color:"var(--ach-red)", background:"var(--ach-red-tint)", padding:8, borderRadius:6, fontSize:13}}>{err}</div>
        )}
      </div>

      <div className="ach-card" style={{marginBottom: 16}}>
        <h2 className="ach-card__title">Upload a filled-in file</h2>
        <div style={{display:"flex", gap:10, alignItems:"center", flexWrap:"wrap"}}>
          <input type="file" accept=".csv,text/csv" onChange={(e)=>{ setFile(e.target.files[0]); setPreview(null); setResult(null); setErr(""); }} data-testid="bi-file" />
          <button className="ach-btn ach-btn--primary" onClick={runPreview} disabled={!file || busy} data-testid="bi-preview">
            {busy ? "Checking…" : "Check the file →"}
          </button>
        </div>
        {err && !err.startsWith("Couldn't download the template") && <div style={{marginTop:12, color:"var(--ach-red)", background:"var(--ach-red-tint)", padding:8, borderRadius:6, fontSize:13}}>{err}</div>}
      </div>

      {preview && (
        <div className="ach-card" data-testid="bi-preview-card">
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12}}>
            <div>
              <h2 className="ach-card__title" style={{margin:0}}>Preview — nothing has been imported yet</h2>
              <div style={{fontSize:12, color:"var(--ach-text-2)", marginTop:4}}>
                {preview.total} rows in the file · <strong style={{color:"var(--ach-green)"}}>{preview.ok_count} ready to import</strong> · <strong style={{color:"var(--ach-red)"}}>{preview.skip_count} will be skipped</strong>
              </div>
            </div>
            {preview.ok_count > 0 && (
              <button className="ach-btn ach-btn--primary" onClick={commit} disabled={committing} data-testid="bi-commit">
                {committing ? "Importing…" : `Import the ${preview.ok_count} good row${preview.ok_count === 1 ? "" : "s"} →`}
              </button>
            )}
          </div>
          <div className="ach-table-wrap">
            <table className="ach-table">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Unit</th>
                  <th>Ready?</th>
                  <th>What we&apos;ll import (or why it&apos;s skipped)</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map(r => (
                  <tr key={r.row_number} data-testid={`bi-row-${r.row_number}`} style={r.ok ? undefined : {background:"var(--ach-red-tint)"}}>
                    <td>{r.row_number}</td>
                    <td>{r.unit_number || <em>—</em>}</td>
                    <td>{r.ok
                      ? <span style={{color:"var(--ach-green)", fontWeight:600}}>✓ Ready</span>
                      : <span style={{color:"var(--ach-red)", fontWeight:600}}>✗ Skip</span>}</td>
                    <td>
                      {r.ok
                        ? <div>
                            <span style={{color:"var(--ach-text-2)", fontSize:12}}>
                              {r.cleaned.make} {r.cleaned.model_name} · {r.cleaned.capacity_tonnes}t · {r.cleaned.owning_company} · {r.cleaned.status}
                            </span>
                            {r.notes && r.notes.length > 0 && (
                              <ul data-testid={`bi-row-${r.row_number}-notes`} style={{margin:"6px 0 0", paddingLeft:18, color:"var(--ach-amber, #b45309)", fontSize:12}}>
                                {r.notes.map((n,i) => <li key={i}>Note: {n}</li>)}
                              </ul>
                            )}
                          </div>
                        : <ul style={{margin:0, paddingLeft:18, color:"var(--ach-red)", fontSize:12}}>
                            {r.errors.map((e,i) => <li key={i}>{e}</li>)}
                          </ul>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {result && (
        <div className="ach-card" data-testid="bi-result-card" style={{background:"var(--ach-green-tint)", borderLeft:"4px solid var(--ach-green)"}}>
          <h2 className="ach-card__title" style={{margin:0}}>{result.message}</h2>
          {result.inserted_count > 0 && (
            <div style={{marginTop:10, fontSize:13, color:"var(--ach-text-1)"}}>
              <strong>Now in the fleet:</strong> {result.inserted.join(", ")}
            </div>
          )}
          {result.notes && result.notes.length > 0 && (
            <div style={{marginTop:10}} data-testid="bi-result-notes">
              <div style={{fontSize:13, color:"var(--ach-amber, #b45309)", fontWeight:600, marginBottom:6}}>
                Adjustments we made while importing:
              </div>
              <ul style={{margin:0, paddingLeft:20, fontSize:12, color:"var(--ach-text-1)", lineHeight:1.6}}>
                {result.notes.map(n => (
                  <li key={n.row_number}>Row {n.row_number} ({n.unit_number}): {n.notes.join(", ")}</li>
                ))}
              </ul>
            </div>
          )}
          {result.skipped_count > 0 && (
            <div style={{marginTop:10}}>
              <div style={{fontSize:13, color:"var(--ach-red)", fontWeight:600, marginBottom:6}}>Skipped rows — fix these and re-upload:</div>
              <ul style={{margin:0, paddingLeft:20, fontSize:12, color:"var(--ach-text-1)", lineHeight:1.6}}>
                {result.skipped.map(s => (
                  <li key={s.row_number}>Row {s.row_number} ({s.unit_number || "—"}): {s.errors.join(", ")}</li>
                ))}
              </ul>
            </div>
          )}
          <button className="ach-btn ach-btn--primary" style={{marginTop:14}} onClick={()=>nav("/machines")} data-testid="bi-view-machines">See the updated fleet →</button>
        </div>
      )}
    </div>
  );
}
